import React, { useEffect, useState } from 'react'
import { Modal, Button, Form, Row, Col, Nav, Tab } from 'react-bootstrap'
import { PhoneOff } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchLanguages } from '../redux/languagesSlice'
import { RootState } from '../redux/store'
import CallTypeSelector from './molecules/CallTypeSelector'
import GenderSelector from './molecules/GenderSelector'
import LanguageSelector from './molecules/LanguageSelector'
import AIInterpretationFields from './molecules/AIInterpretationFields'
import ToggleSwitch from './atoms/ToggleSwitch'
import { useConsumerWidget } from '../context/ConsumerWidgetContext'
import { useAuth } from '../modules/auth'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL


interface ConnectCallModalProps {
  show: boolean
  onHide: () => void
  onSubmit: () => void
}

const ConnectCallModal: React.FC<ConnectCallModalProps> = ({ show, onHide, onSubmit }) => {
  const dispatch = useDispatch()
  const {currentUser} = useAuth()
  const [isLoading, setIsLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [activeTab, setActiveTab] = useState('on-demand');
  const [isValidatingPin, setIsValidatingPin] = useState(false);
  const [pinValidationError, setPinValidationError] = useState('');
  const [pinValidationSuccess, setPinValidationSuccess] = useState(false);
  const [videoDisabled, setVideoDisabled] = useState(false);
  const [isLoadingAccountDetails, setIsLoadingAccountDetails] = useState(false);
  const [accountDetailsFetched, setAccountDetailsFetched] = useState(false);
  const [accountDetails, setAccountDetails] = useState<any>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [recordingTranscriptionStatus, setRecordingTranscriptionStatus] = useState<{isRecording?: boolean; isTranscription?: boolean} | null>(null);
  const [recordingTranscriptionStatusFetched, setRecordingTranscriptionStatusFetched] = useState(false);
  const [isLoadingRecordingTranscriptionStatus, setIsLoadingRecordingTranscriptionStatus] = useState(false);
  const [cameraPermissionError, setCameraPermissionError] = useState('');

  // Get form state from context - must be declared before useEffects that use these values
  const {
    callType,
    setCallType,
    gender,
    setGender,
    language,
    setLanguage,
    transcribe,
    setTranscribe,
    record,
    setRecord,
    meetingPin,
    setMeetingPin,
    callMode,
    setCallMode,
  } = useConsumerWidget()

  // const { items: languages, loading: isLoading } = useSelector(
  //   (state: RootState) => state.languages
  // )

  const fetchRequesterLanguages = async () => {
    if (languages.length > 0) return; // Avoid refetching if languages are already loaded
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/accounts/requester-language`)
      console.log("testing payload", response?.data)
      const transformed = response?.data?.data.map((item: any) => ({
        key: item.languageToName,
        value: item.languageToName,
      }));
      setLanguages(transformed);
      
      // Set the first language as default if no language is selected or if current language is not in the list
      if (transformed.length > 0 && (!language || !transformed.find((lang: any) => lang.key === language))) {
        setLanguage(transformed[0].key);
      }
      
      setIsLoading(false);

    } catch (error) {
      console.error(error)
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchRequesterLanguages();
  }, [])

  useEffect(() => {
    if (!show) return

    const hasOpiOrVri =
      currentUser?.result?.opI_ShdTelephonic ||
      currentUser?.result?.opI_OndemandTelephonic ||
      currentUser?.result?.vrI_ShdVideoInteroreting ||
      currentUser?.result?.vrI_OndemandVideoInteroreting
    const hasAiPermission =
      currentUser?.result?.aiInterpretationPermission === true ||
      (currentUser?.result as any)?.AiInterpretationPermission === true

    if (!hasOpiOrVri && hasAiPermission) {
      setActiveTab('on-demand')
      setCallMode('ai-interpretation')
    }
  }, [show, currentUser, setCallMode])

  const ensureCameraPermission = async (): Promise<boolean> => {
    setCameraPermissionError('')

    // If the browser can't prompt, treat as not available.
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraPermissionError('Camera access is not supported in this browser.')
      return false
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({video: true})
      stream.getTracks().forEach((t) => t.stop())
      return true
    } catch (e: any) {
      const name = e?.name ? String(e.name) : ''
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setCameraPermissionError('Camera permission was denied. Please allow camera access in your browser settings to start a video call.')
      } else if (name === 'NotFoundError') {
        setCameraPermissionError('No camera was found on this device.')
      } else if (name === 'NotReadableError') {
        setCameraPermissionError('Camera is currently in use by another application. Close other apps using the camera and try again.')
      } else {
        setCameraPermissionError('Unable to access camera. Please check permissions and try again.')
      }
      return false
    }
  }

  // Fetch account details when modal opens to check video permissions
  useEffect(() => {
    const fetchAccountDetails = async () => {
      // Don't fetch if data already exists
      if (accountDetailsFetched && accountDetails) {
        // Use existing data to set video disabled state
        const vrI_OndemandVideoInteroreting = accountDetails.vrI_OndemandVideoInteroreting || false;
        const vrI_ShdVideoInteroreting = accountDetails.vrI_ShdVideoInteroreting || false;
        
        // Set accountId from cached accountDetails if not already set
        if (!accountId) {
          const id = accountDetails?.id || accountDetails?.code || null;
          if (id) {
            setAccountId(id);
          }
        }
        
        if (!vrI_OndemandVideoInteroreting && !vrI_ShdVideoInteroreting) {
          setVideoDisabled(true);
          if (callType === 'video') {
            setCallType('audio');
          }
        } else {
          setVideoDisabled(false);
        }
        return;
      }

      if (show && currentUser?.result?.code) {
        setIsLoadingAccountDetails(true);
        try {
          const response = await axios.get(`${API_URL}/accounts/GetAccountDetailByID/${currentUser.result.code}`);
          const accountData = response.data?.data || response.data;
          
          // Store account details in state
          setAccountDetails(accountData);
          setAccountDetailsFetched(true);
          
          // Extract the id from account data
          const id = accountData?.id || accountData?.code || null;
          setAccountId(id);
          
          // Check if both video fields are false
          const vrI_OndemandVideoInteroreting = accountData.vrI_OndemandVideoInteroreting || false;
          const vrI_ShdVideoInteroreting = accountData.vrI_ShdVideoInteroreting || false;
          
          // If both are false, disable video
          if (!vrI_OndemandVideoInteroreting && !vrI_ShdVideoInteroreting) {
            setVideoDisabled(true);
            // If video is currently selected, switch to audio
            if (callType === 'video') {
              setCallType('audio');
            }
          } else {
            setVideoDisabled(false);
          }
        } catch (error) {
          console.error('Error fetching account details:', error);
          // On error, allow video (default behavior)
          setVideoDisabled(false);
          setAccountDetailsFetched(true); // Mark as fetched even on error to prevent retries
        } finally {
          setIsLoadingAccountDetails(false);
        }
      }
    };

    if (show) {
      fetchAccountDetails();
    }
  }, [show, currentUser?.result?.code, accountDetailsFetched, accountDetails, callType, setCallType])

  // Fetch recording and transcription status when modal opens
  useEffect(() => {
    const fetchRecordingTranscriptionStatus = async () => {
      // Don't fetch if data already exists (same pattern as account details)
      if (recordingTranscriptionStatusFetched && recordingTranscriptionStatus) {
        return;
      }

      // Get customerId from accountDetails or currentUser
      const customerId = accountDetails?.fK_Customer || currentUser?.result?.fK_Customer || null;
      
      if (show && customerId) {
        setIsLoadingRecordingTranscriptionStatus(true);
        try {
          const response = await axios.get(`${API_URL}/customer/recording-transcription-status`, {
            params: { customerId: customerId }
          });
          const data = response.data?.data || response.data;
          setRecordingTranscriptionStatus(data);
          setRecordingTranscriptionStatusFetched(true);
        } catch (error) {
          console.error('Error fetching recording/transcription status:', error);
          // On error, set default values (both false) to hide toggles
          setRecordingTranscriptionStatus({ isRecording: false, isTranscription: false });
          setRecordingTranscriptionStatusFetched(true);
        } finally {
          setIsLoadingRecordingTranscriptionStatus(false);
        }
      }
    };

    if (show) {
      fetchRecordingTranscriptionStatus();
    }
  }, [show, accountDetails, currentUser?.result?.fK_Customer, recordingTranscriptionStatusFetched, recordingTranscriptionStatus]);


  // Handle meeting pin input change (no validation on change)
  const handleMeetingPinChange = (value: string) => {
    setMeetingPin(value);
    // Clear validation states when user types
    setPinValidationError('');
    setPinValidationSuccess(false);
  };

  // Clear meeting pin and validation states
  const clearMeetingPinState = () => {
    setMeetingPin('');
    setPinValidationError('');
    setPinValidationSuccess(false);
    setIsValidatingPin(false);
  };

  // Handle tab change
  const handleTabChange = (tabKey: string | null) => {
    const tab = tabKey || 'on-demand'
    setActiveTab(tab)
    if (tab === 'meeting-pin') {
      setCallMode('standard')
    } else if (!hasAnyPermission && hasAiInterpretationPermission) {
      setCallMode('ai-interpretation')
    }
    // Clear meeting pin state when switching to On Demand Call tab
    if (tab === 'on-demand') {
      clearMeetingPinState()
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    clearMeetingPinState();
    setCameraPermissionError('')
    setCallMode('standard')
    setActiveTab('on-demand')
    onHide();
  };


  // Reset account details and recording/transcription status when user changes
  useEffect(() => {
    setAccountDetailsFetched(false);
    setAccountDetails(null);
    setAccountId(null);
    setVideoDisabled(false);
    setRecordingTranscriptionStatusFetched(false);
    setRecordingTranscriptionStatus(null);
  }, [currentUser])
  
  // Handle record toggle changes
  const handleRecordChange = (value: boolean) => {
    setRecord(value)
    // If record is turned off, also turn off transcribe
    if (!value) {
      setTranscribe(false)
    }
  }

  const selectedCallType = callMode === 'ai-interpretation' ? 'ai-interpretation' : callType

  const handleCallTypeChange = (type: 'audio' | 'video' | 'ai-interpretation') => {
    if (type === 'ai-interpretation') {
      if (!hasAiInterpretationPermission) return
      setCallMode('ai-interpretation')
      setCallType('audio')
      setCameraPermissionError('')
      return
    }

    setCallMode('standard')
    setCallType(type)
  }

  // Load languages when modal opens
  // useEffect(() => {
  //   if (show && languages.length === 0) {
  //     dispatch(fetchLanguages() as any)
  //   }
  // }, [show, languages.length, dispatch])

  // Set default language when languages are loaded
  // useEffect(() => {
  //   if (languages.length > 0 && !language) {
  //     const defaultLang = languages.find((lang) => lang.key === 'EN') || languages[0]
  //     setLanguage(defaultLang.key)
  //   }
  // }, [languages, language, setLanguage])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setCameraPermissionError('')

    if (activeTab === 'meeting-pin') {
      if (!meetingPin.trim()) {
        setPinValidationError('Please enter a meeting pin');
        return;
      }

      // Validate meeting pin before proceeding
      setIsValidatingPin(true);
      setPinValidationError('');
      
      try {
        const response = await axios.post(`${API_URL}/Appoinment/validate-meeting-pin`, {
          meetingPin: meetingPin.trim()
        });

        if (response.data.status === 'S' && response.data.result?.isValid) {
          setPinValidationSuccess(true);
          setPinValidationError('');
          // If video is selected, ensure camera permission before proceeding
          if (callType === 'video') {
            const ok = await ensureCameraPermission()
            if (!ok) return
          }
          onSubmit()
        } else {
          setPinValidationError(response.data.result?.message  || 'Invalid meeting pin');
          setPinValidationSuccess(false);
        }
      } catch (error: any) {
        console.error('Error validating meeting pin:', error);
        const errorMessage = error.response?.data?.result?.message || 
                            error.response?.data?.text || 
                            'Failed to validate meeting pin';
        setPinValidationError(errorMessage);
        setPinValidationSuccess(false);
      } finally {
        setIsValidatingPin(false);
      }
    } else if (callMode === 'ai-interpretation') {
      setCallMode('ai-interpretation')
      onSubmit()
    } else {
      if (callType === 'video') {
        const ok = await ensureCameraPermission()
        if (!ok) return
      }
      onSubmit()
    }
  }

  const hasAnyPermission =
    currentUser?.result?.opI_ShdTelephonic ||
    currentUser?.result?.opI_OndemandTelephonic ||
    currentUser?.result?.vrI_ShdVideoInteroreting ||
    currentUser?.result?.vrI_OndemandVideoInteroreting

  const hasAiInterpretationPermission =
    currentUser?.result?.aiInterpretationPermission === true ||
    (currentUser?.result as any)?.AiInterpretationPermission === true
  const canUseModal = hasAnyPermission || hasAiInterpretationPermission
  const aiInterpretationDisabledTooltip =
    'AI interpretation is not allowed for your account. Kindly contact AdAstra administration for assistance.'

  const isFormValid =
    activeTab === 'on-demand' && callMode === 'ai-interpretation'
      ? !!language
      : !!(callType && gender && language && (activeTab === 'on-demand' || meetingPin.trim() !== ''))

  const tabLinkStyle = (tabKey: string) => ({
    backgroundColor: activeTab === tabKey ? '#D7383C' : 'transparent',
    borderColor: activeTab === tabKey ? '#D7383C' : '#dee2e6',
    color: activeTab === tabKey ? 'white' : 'inherit',
    fontWeight: activeTab === tabKey ? '600' : 'normal',
  })

  const renderCallOptions = (recordToggleId: string, transcribeToggleId: string) => (
    recordingTranscriptionStatus?.isRecording && (
      <Col xs={12}>
        <div className='mt-4'>
          <h6 className='mb-3 fw-bold text-gray-800'>Call Options</h6>
          <div className='p-3 border rounded bg-light'>
            {isLoadingRecordingTranscriptionStatus ? (
              <div className='d-flex align-items-center justify-content-center p-2'>
                <div className='spinner-border spinner-border-sm text-primary me-2' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <span className='text-muted'>Loading call options...</span>
              </div>
            ) : (
              <Row className='g-3'>
                {recordingTranscriptionStatus?.isRecording && (
                  <Col xs={12} sm={record ? 6 : 12}>
                    <ToggleSwitch
                      id={recordToggleId}
                      label='Record Call'
                      checked={record}
                      onChange={handleRecordChange}
                      helpText='Save call recording'
                    />
                  </Col>
                )}

                {recordingTranscriptionStatus?.isTranscription && record && (
                  <Col xs={12} sm={6}>
                    <ToggleSwitch
                      id={transcribeToggleId}
                      label='Transcribe Call'
                      checked={transcribe}
                      onChange={setTranscribe}
                      helpText='Enable Transcription'
                    />
                  </Col>
                )}
              </Row>
            )}
          </div>
        </div>
      </Col>
    )
  )

  return (
    <Modal show={show} onHide={handleModalClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Call For Interpretation</Modal.Title>
      </Modal.Header>

      {!canUseModal ? (
        <Modal.Body>
          <div className='d-flex flex-column align-items-center justify-content-center py-5'>
            <div
              className='d-flex align-items-center justify-content-center rounded-circle mb-4'
              style={{ width: 64, height: 64, backgroundColor: '#FEE2E2' }}
            >
              <PhoneOff size={32} color='#DC2626' />
            </div>
            <p
              className='text-center fw-semibold mb-0 px-4'
              style={{ color: '#374151', fontSize: '15px', lineHeight: '1.6' }}
            >
              You don't have over the phone Interpretation permissions. Please contact <span className='text-danger fw-bold'>AdAstra Team</span>.
            </p>
          </div>
        </Modal.Body>
      ) : (
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {isLoadingAccountDetails || (!accountDetailsFetched && show) ? (
            <div className='d-flex align-items-center justify-content-center p-5' style={{ minHeight: '300px' }}>
              <div className='text-center'>
                <div className='spinner-border text-primary mb-3' role='status' style={{ width: '3rem', height: '3rem' }}>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <div className='text-muted'>Loading account details...</div>
              </div>
            </div>
          ) : (
            <div className='p-4'>
              {cameraPermissionError && (
                <div className='alert alert-danger mb-4' role='alert'>
                  {cameraPermissionError}
                </div>
              )}
              {/* Tab Navigation */}
              <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
                <Nav variant="tabs" className="mb-4">
                  {canUseModal && (
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="on-demand"
                      style={tabLinkStyle('on-demand')}
                    >
                      On Demand Call
                    </Nav.Link>
                  </Nav.Item>
                  )}
                  {hasAnyPermission && (
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="meeting-pin"
                      style={tabLinkStyle('meeting-pin')}
                    >
                      Meeting Pin
                    </Nav.Link>
                  </Nav.Item>
                  )}
                </Nav>

                <Tab.Content>
                  {/* On Demand Call Tab */}
                  <Tab.Pane eventKey="on-demand">
                    <Row className='g-4'>
                      {/* Call Type Selection */}
                      <Col xs={12}>
                        <div className='mb-3'>
                          <CallTypeSelector 
                            selectedType={selectedCallType} 
                            onTypeChange={handleCallTypeChange}
                            videoDisabled={videoDisabled}
                            aiInterpretationVisible={true}
                            aiInterpretationEnabled={hasAiInterpretationPermission}
                            aiInterpretationDisabledTooltip={aiInterpretationDisabledTooltip}
                            standardCallEnabled={!!hasAnyPermission}
                          />
                        </div>
                      </Col>

                    {callMode === 'ai-interpretation' ? (
                      <Col xs={12}>
                        <AIInterpretationFields
                          language={language}
                          onLanguageChange={setLanguage}
                          languages={languages}
                          loading={isLoading}
                        />
                      </Col>
                    ) : (
                      <Col xs={12}>
                        <Row className='g-3'>
                          <Col xs={12} md={12}>
                            <GenderSelector selectedGender={gender} onGenderChange={setGender} />
                          </Col>
                          <Col xs={12} md={12}>
                            <LanguageSelector
                              selectedLanguage={language}
                              onLanguageChange={setLanguage}
                              languages={languages}
                              loading={isLoading}
                            />
                          </Col>
                        </Row>
                      </Col>
                    )}

                    {/* Call Options - Only show if Call recording is enabled */}
                    {renderCallOptions('record-toggle', 'transcribe-toggle')}
                  </Row>
                </Tab.Pane>

                {/* Meeting Pin Tab */}
                <Tab.Pane eventKey="meeting-pin">
                  <Row className='g-4'>
                    <Col xs={12}>
                      <label className='fw-bold form-label'>Meeting Pin</label>
                      <div className='position-relative'>
                        <input 
                          className={`form-control custom-input-height ${pinValidationError ? 'is-invalid' : ''} ${pinValidationSuccess ? 'is-valid' : ''}`}
                          placeholder='Enter Your Meeting Pin'
                          value={meetingPin}
                          onChange={(e) => handleMeetingPinChange(e.target.value)}
                        />
                      </div>
                      
                      {/* Validation Messages */}
                      {pinValidationError && (
                        <div className='invalid-feedback d-block mt-2'>
                          <i className='bi bi-exclamation-circle-fill text-danger me-2'></i>
                          {pinValidationError}
                        </div>
                      )}
                      
                      {pinValidationSuccess && (
                        <div className='valid-feedback d-block mt-2'>
                          <i className='bi bi-check-circle-fill text-success me-2'></i>
                          Meeting pin is valid. You can join the meeting now.
                        </div>
                      )}
                    </Col>

                    {/* Call Options - Only show if Call recording is enabled */}
                    {renderCallOptions('record-toggle-meeting', 'transcribe-toggle-meeting')}
                  </Row>
                </Tab.Pane>

              </Tab.Content>
            </Tab.Container>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant='secondary' onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant='primary' type='submit' disabled={!isFormValid || isLoading || isValidatingPin || isLoadingAccountDetails || (!accountDetailsFetched && show)}>
            {isLoading ? 'Loading Languages...' : isValidatingPin ? 'Validating Pin...' : isLoadingAccountDetails ? 'Loading...' : 'Start Call'}
          </Button>
        </Modal.Footer>
      </Form>
      )}
    </Modal>
  )
}

export default ConnectCallModal
